/**
 * PlaceStamp — decorative round stamp shown to the left of every
 * location name card (state / county / city / town / village /
 * district).
 *
 * Lookup order:
 *   1. Guide-uploaded override on the location doc (`stamp_url`).
 *   2. Named lookup by last-slug-segment against the India stamp
 *      manifest (real illustrations for 28 states, 14 Kerala
 *      districts, 48 cities).
 *   3. US pool: deterministic 12-stamp hash per kind
 *      (state / county / city / town / village).
 *   4. Lucide MapPin fallback.
 */
import { MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import INDIA_STAMPS from "@/data/india_stamps.json";
import { placeKindTag } from "@/components/PlaceKindTag";
import { directorySlugFor } from "@/pages/KindDirectoryPage";
import { http } from "@/lib/api";

// In-memory cache so multiple <PlaceStamp autoFetch/> instances for
// the same slug share one network call per session.
const STAMP_CACHE = new Map();  // slug → override url or null

/** Prime / evict the stamp cache — used after guide uploads / clears
 * so the very next render picks the fresh crest without a hard reload. */
export function primeStampCache(slug, url) {
  if (!slug) return;
  STAMP_CACHE.set(slug, url || null);
}
export function evictStampCache(slug) {
  if (!slug) return;
  STAMP_CACHE.delete(slug);
}

// Categories that have the US stamp pool artwork on disk.
const US_POOL = { state: 12, county: 12, city: 12, town: 12, village: 12 };
const US_FOLDER = { state: "state", county: "county", city: "city", town: "city", village: "city" };

// Flatten India manifest into sets for O(1) named lookups.
const IN_CITIES    = new Set(INDIA_STAMPS.cities);
const IN_DISTRICTS = new Set(INDIA_STAMPS.districts);
const IN_STATES    = new Set(INDIA_STAMPS.states);

/** Cheap stable string hash → non-negative int. */
function hashString(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function lastSeg(slug) {
  return String(slug || "").split("/").filter(Boolean).pop() || "";
}

/**
 * Given a location kind + slug, return the URL of its illustrated
 * stamp, or `null` if no artwork is available. Callers should
 * render a lucide fallback in that case.
 */
export function stampUrlFor(kind, slug, override) {
  if (override) return override;

  const k = (kind || "").toLowerCase();
  const seg = lastSeg(slug);

  // 1) Named India lookups — kind-first, else best match by name.
  if (k === "state" && IN_STATES.has(seg))       return `/place-stamps/in-state-${seg}.png`;
  if (k === "district" && IN_DISTRICTS.has(seg)) return `/place-stamps/in-district-${seg}.png`;
  if ((k === "city" || k === "town") && IN_CITIES.has(seg)) return `/place-stamps/in-city-${seg}.png`;
  // Kind-agnostic: match on name even when kind is unusual (e.g.
  // "district" recorded as "county" for Indian districts).
  if (IN_STATES.has(seg))    return `/place-stamps/in-state-${seg}.png`;
  if (IN_DISTRICTS.has(seg)) return `/place-stamps/in-district-${seg}.png`;
  if (IN_CITIES.has(seg))    return `/place-stamps/in-city-${seg}.png`;

  // 2) US hash pool
  const folder = US_FOLDER[k];
  const count = US_POOL[k];
  if (folder && count) {
    const idx = (hashString(String(slug || k)) % count) + 1;
    const n = String(idx).padStart(2, "0");
    return `/place-stamps/${folder}-${n}.png`;
  }

  return null;
}

/**
 * PlaceStamp — square rounded stamp with graceful fallback.
 *
 *   kind:     location kind (state / county / district / city / town / village)
 *   slug:     location slug (used both for named lookup and hash pool)
 *   override: URL of a Guide-uploaded custom stamp (wins over both pools)
 *   size:     pixel dimension (defaults 40)
 */
export default function PlaceStamp({
  kind, slug, override, size = 40, className = "", title, testid,
  caption = false,        // when true, render "US STATE" text below the stamp
  captionCountry,         // override for the country used in the caption
  linkCaption = false,    // wrap stamp + caption in a <Link> to /directory/<kind>
  autoFetch = false,      // fetch guide-uploaded override for this slug at mount
}) {
  const [fetched, setFetched] = useState(() => (autoFetch && slug ? STAMP_CACHE.get(slug) : undefined));

  useEffect(() => {
    if (!autoFetch || !slug) return;
    if (STAMP_CACHE.has(slug)) {
      setFetched(STAMP_CACHE.get(slug));
      return;
    }
    let alive = true;
    http.get(`/geo/places/stamp/${slug}`)
      .then((r) => {
        const url = r.data?.stamp_url || null;
        STAMP_CACHE.set(slug, url);
        if (alive) setFetched(url);
      })
      .catch(() => {
        STAMP_CACHE.set(slug, null);
        if (alive) setFetched(null);
      });
    return () => { alive = false; };
  }, [autoFetch, slug]);

  const effectiveOverride = override || fetched || null;
  const url = stampUrlFor(kind, slug, effectiveOverride);

  const img = url ? (
    <img
      src={url}
      alt={title || `${kind} stamp`}
      title={title}
      loading="lazy"
      className={`rounded-full object-cover shrink-0 ${className}`}
      style={{ width: size, height: size }}
      data-testid={testid || `place-stamp-${kind}`}
    />
  ) : (
    <div
      className={`rounded-full bg-slate-100 flex items-center justify-center shrink-0 ${className}`}
      style={{ width: size, height: size }}
      title={title}
      data-testid={testid || "place-stamp-fallback"}
    >
      <MapPin className="opacity-60" style={{ width: size * 0.5, height: size * 0.5 }} />
    </div>
  );

  if (!caption) return img;

  const captionText = placeKindTag(kind, slug, captionCountry) || "";
  const dir = linkCaption ? directorySlugFor(kind, captionCountry || slug) : null;
  const stack = (
    <span className="inline-flex flex-col items-center gap-1.5">
      {img}
      {captionText && (
        <span
          className="text-xs sm:text-sm font-mono font-semibold uppercase tracking-widest leading-tight opacity-90 whitespace-nowrap"
          data-testid={`place-stamp-caption-${(kind || "").toLowerCase()}`}
        >
          {captionText}
        </span>
      )}
    </span>
  );
  if (dir) {
    return (
      <Link
        to={`/directory/${dir}`}
        className="inline-flex hover:opacity-80 transition"
        title={`Browse all ${captionText}s`}
      >
        {stack}
      </Link>
    );
  }
  return stack;
}
